import { jsx } from "panel";
import produce from "immer";
import capitalize from "lodash/capitalize";
import clamp from "lodash/clamp";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { QueryBlockEl } from "common/query-builder/query-block";
import { MenuItemType } from "common/query-builder/query-block/properties-menu/types";
import { EntryPart } from "common/query-builder/query-entry-section/types";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { TimeUnit, TimeUnitLabels } from "common/widgets/date-range-picker/models/types";
import * as PropertiesMenu from "common/query-builder/query-block/properties-menu/types";
import { BEHAVIOR_AGGREGATION } from "common/query-builder/query-block/properties-menu/constants";
import {
  propertySelectionToSegmentation,
  segmentationToPropertySelection
} from "common/query-builder/query-block/segment-menu/util";
import { RowFilters } from "common/query-builder/query-entry-section/components/filters";
import { DisplayContext as PropDisplayContext } from "common/widgets/property-filter-menu/models/types";
import { PropertyFilterFactory } from "common/widgets/property-filter-menu/models/utils";
import { InputEl } from "common/components/input";
import { InputSize, InputType } from "common/components/input/types";
import { parseNumber } from "common/util";
import { reporter } from "common/report/error-reporting";
import {
  MaxMultiAttributionWindowDays,
  MinMultiAttributionWindowDays,
  NumMultiAttributionWindowSessions
} from "./constants";
import {
  DEFAULT_AGGREGATION_BEHAVIOR,
  DEFAULT_BEHAVIORLESS_SEGMENTATION,
  DEFAULT_FREQUENCY_BEHAVIOR,
  DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
  generateAggregationsList,
  getAggregationLabelForBehavior,
  getColumnLabelForBehavior,
  getDefaultPropertyValueForBehavior,
  isAggregationBehavior,
  isEventBasedBehavioralSegmentation,
  isMultiAttributionSegmentation
} from "./utils";
export function BehavioralSubItem({
  selected,
  handleSelect,
  index,
  isPlaceholder,
  section,
  selectorIcon,
  entry,
  displayCohorts = true,
  isForFilter = false
}) {
  const currentSelection = selected;
  const behavior = currentSelection.behavior;
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const handleAggregationSelection = (ev) => {
    const { value: aggregation } = ev.detail.item;
    const selection = addBehaviorToSegmentation(currentSelection, {
      ...behavior,
      aggregationOperator: aggregation.operator
    });
    handleSelect({ ...currentSelection, ...selection });
  };
  const sections = generateAggregationsList(behavior);
  const label = getAggregationLabelForBehavior(behavior);
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-query-sub-selector"
  }, isAggregationBehavior(behavior) && /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text.strong"
  }, /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      elref: `aggregatorSelect`,
      "disable-search": true,
      tabindex: -1,
      theme: SelectTheme.Tertiary,
      open: activeIndex === index && activePart === EntryPart.Aggregator,
      label
    },
    props: {
      sections
    },
    on: {
      close: () => section.helpers.setActiveEntry(`close`, index, EntryPart.Aggregator),
      open: () => section.helpers.setActiveEntry(`open`, index, EntryPart.Aggregator),
      change: handleAggregationSelection
    }
  })), /* @__PURE__ */ jsx(PropertySelector, {
    selected,
    handleSelect,
    index,
    isPlaceholder,
    section,
    selectorIcon,
    entry,
    displayCohorts,
    isForFilter,
    isInner: true
  }));
}
export function MultiAttributionSubItem({
  selected,
  handleSelect
}) {
  const { window } = selected.behavior.dateRange;
  const dateRangeLabel = `${window.value} ${capitalize(window.unit)} Lookback Window`;
  const isSessionWindow = window.unit === TimeUnit.Session;
  return /* @__PURE__ */ jsx("div", {
    sel: ".subitem-text"
  }, /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      size: ButtonSize.Inline,
      theme: SelectTheme.Tertiary,
      label: dateRangeLabel
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".multi-attribution-window-content",
    attrs: { slot: `body` }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".multi-attribution-window-title"
  }, "Lookback window"), /* @__PURE__ */ jsx("div", {
    sel: ".multi-attribution-window-description"
  }, "The time window where a user's events with this attribution property are counted towards the calculation. The window ends when the conversion metric happens."), /* @__PURE__ */ jsx("div", {
    sel: ".multi-attribution-window-body"
  }, /* @__PURE__ */ jsx(InputEl, {
    sel: ".multi-attribution-window-value",
    attrs: {
      disabled: isSessionWindow,
      size: InputSize.Medium,
      type: InputType.Number,
      min: isSessionWindow ? NumMultiAttributionWindowSessions.toString() : MinMultiAttributionWindowDays.toString(),
      max: isSessionWindow ? NumMultiAttributionWindowSessions.toString() : MaxMultiAttributionWindowDays.toString(),
      maxlength: 2
    },
    on: {
      change: (ev) => {
        const value = parseNumber(ev.detail.value);
        const updatedSelection = produce(selected, (draft) => {
          const { window: window2 } = draft.behavior.dateRange;
          if (window2.unit === TimeUnit.Day) {
            window2.value = Number.isFinite(value) ? clamp(value, MinMultiAttributionWindowDays, MaxMultiAttributionWindowDays) : MinMultiAttributionWindowDays;
          } else {
            reporter.error(`multi attribution lookback unit change: unexpected TimeUnit`, {
              unit: window2.unit
            });
          }
        });
        handleSelect(updatedSelection);
      }
    },
    props: {
      value: window.value.toString()
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".multi-attribution-window-unit"
  }, `${TimeUnitLabels[TimeUnit.Day]}s`.toLowerCase())))));
}
function addBehaviorToSegmentation(segmentation, behavior) {
  const defaultBehavior = isAggregationBehavior(behavior) ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR;
  return produce(segmentation, (draft) => {
    const behaviorDraft = {
      ...defaultBehavior,
      ...draft.behavior,
      ...behavior
    };
    draft.behavior = behaviorDraft;
    draft.propertyName = getColumnLabelForBehavior(behaviorDraft);
  });
}
function getPropertySelectionFromEventPropUpdate(ev, currentSelection, _isForFilter) {
  const { event, property } = ev.detail.item;
  return addBehaviorToSegmentation(currentSelection, { ...currentSelection.behavior, event, property });
}
function getPropertySelectionFromEventUpdate(ev, currentSelection, _isForFilter) {
  const item = ev.detail.item;
  return addBehaviorToSegmentation(currentSelection, {
    ...currentSelection.behavior,
    event: { label: item.label, value: item.value }
  });
}
function getPropertySelectionFromEvent(ev, currentSelection, isForFilter) {
  const { menuItemType, item, stagedItemsStack } = ev.detail;
  const isMultiAttributionBehavior = stagedItemsStack == null ? void 0 : stagedItemsStack.some(
    (item2) => item2.menuItemType === PropertiesMenu.MenuItemType.BehaviorMultiAttribution
  );
  let selection;
  if (!(stagedItemsStack == null ? void 0 : stagedItemsStack.length) && menuItemType === MenuItemType.BehaviorWithEvent) {
    const isAggregation = item[`value`] === BEHAVIOR_AGGREGATION.value || item[`propertyName`] === BEHAVIOR_AGGREGATION.value;
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      behavior: {
        ...isAggregation ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR,
        event: { custom: false, label: `Select Event`, value: null },
        property: getDefaultPropertyValueForBehavior(isAggregation)
      }
    };
  } else if (isMultiAttributionBehavior) {
    selection = {
      ...propertySelectionToSegmentation(item),
      behavior: {
        ...isMultiAttributionSegmentation(currentSelection) ? currentSelection.behavior : DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
        property: item
      }
    };
  } else if (isForFilter) {
    selection = PropertyFilterFactory.createPropertyFilter(item, {
      displayContext: PropDisplayContext.Default
    });
  } else {
    selection = propertySelectionToSegmentation(item);
  }
  return selection;
}
export function PropertySelector({
  entry,
  handleSelect,
  index,
  isPlaceholder,
  section,
  selectorIcon,
  displayCohorts = true,
  isForFilter = false,
  isInner = false,
  selected
}) {
  var _a, _b, _c, _d;
  const { index: activeIndex, part: activePart } = section.state.openEntry;
  const queryBlockConfig = section.getQueryBlockConfig(entry.type);
  const entryPart = isInner ? section.helpers.EntryPart.SubValue : section.helpers.EntryPart.Value;
  const behavior = Array.isArray(selected) ? null : selected == null ? void 0 : selected.behavior;
  const isEventProp = isInner && isAggregationBehavior(behavior);
  const isEvent = isInner && !isEventProp;
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(QueryBlockEl, {
    key: "entry-selector",
    class: {
      "is-disabled": entry.readOnlyEvent,
      "is-placeholder": isPlaceholder && selected === null,
      "subitem-style": isInner
    },
    attrs: {
      selected: isForFilter ? { ...selected } : segmentationToPropertySelection(selected),
      elref: `entrySelector`,
      "apply-button-text": `Add ${section.attr(`section-title`)}`,
      "disable-value-selection": true,
      "min-selection": 1,
      "prefix-label": (_a = entry.prefixLabel) != null ? _a : void 0,
      "all-data-groups": section.attr(`data-groups-enabled`),
      "cohort-multiselect": true,
      "cohort-operation-type": isForFilter ? `filter` : `string`,
      "data-group-id": section.attr(`data-group-id`),
      "disabled-items": section.attr(`disabled-items`),
      "enable-enhanced-list-filters": section.attr(`enable-enhanced-list-filters`),
      "enable-workspace-filter-restrictions": section.attr(`enable-workspace-filter-restrictions`),
      "event-names": section.attr(`event-names`),
      "exclude-custom-events": section.attr(`exclude-custom-events`),
      "exclude-dimension-properties": section.attr(`exclude-dimension-properties`),
      "hide-edit-controls": section.attr(`hide-edit-controls`),
      "hide-events": section.attr(`hide-events`),
      "hide-special-date-property-unit": section.attr(`hide-special-date-property-unit`),
      "include-custom-properties": section.attr(`include-custom-properties`),
      "inline-cohorts-override": section.attr(`inline-cohorts-override`),
      "is-multi-attribution": isMultiAttributionSegmentation(selected),
      "project-has-cohorts": displayCohorts && section.attr(`project-has-cohorts`),
      "project-has-people": section.attr(`project-has-people`),
      "project-id": section.attr(`project-id`),
      "property-combine-mode": (_b = section.attr(`property-combine-mode`)) != null ? _b : void 0,
      readonly: section.attr(`readonly`),
      "selector-icon": selectorIcon != null ? selectorIcon : ``,
      "selector-type": `button`,
      "show-special-dataset-property": section.attr(`show-special-dataset-property`),
      "show-special-date-property": section.attr(`show-special-date-property`),
      "support-b2b-cohorts": section.attr(`support-b2b-cohorts`),
      "track-name": section.attr(`track-name`),
      "user-id": section.attr(`user-id`),
      "workspace-id": section.attr(`workspace-id`),
      autofocus: index === 0 && section.attr(`autofocus`) && isPlaceholder && !section.isStagedEntry(0),
      disabled: section.attr(`disabled`),
      open: isPlaceholder && activeIndex === index && activePart === entryPart,
      "show-all-tab": true,
      "button-size": isInner ? ButtonSize.Small : ButtonSize.Medium,
      theme: section.hasNewQBUI() ? ButtonTheme.QueryBuilder : entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select,
      ...queryBlockConfig,
      ...isEvent ? {
        type: `events`,
        selected: (_c = behavior == null ? void 0 : behavior.event) != null ? _c : { custom: false, label: `Select Event`, value: null }
      } : {},
      ...isEventProp ? {
        type: `breakdown`,
        selected: {
          event: (_d = behavior == null ? void 0 : behavior.event) != null ? _d : { custom: false, label: `Select Event`, value: null },
          property: behavior == null ? void 0 : behavior.property
        }
      } : {}
    },
    props: { contextPanelParentElement: section.helpers.getContextPanelParentEl(index) },
    on: {
      close: () => section.helpers.setActiveEntry(`close`, index, entryPart),
      open: () => section.helpers.setActiveEntry(`open`, index, entryPart),
      remove: () => section.helpers.onEventActionClick(section.helpers.Action.Remove, index),
      track: (ev) => section.helpers.trackEntrySelectorEvent(ev, index),
      select: (ev) => {
        var _a2;
        const segment = selected;
        const handledEvent = isEvent ? getPropertySelectionFromEventUpdate(ev, segment, isForFilter) : isEventProp ? getPropertySelectionFromEventPropUpdate(ev, segment, isForFilter) : getPropertySelectionFromEvent(
          ev,
          segment,
          isForFilter
        );
        return handleSelect(handledEvent, null, (_a2 = ev[`detail`]) == null ? void 0 : _a2.trackingProps);
      }
    }
  }));
}
export function BehavioralSubFilters({ entry, handleSelect, index, section, selected }) {
  if (!isEventBasedBehavioralSegmentation(selected)) {
    return null;
  }
  const behavior = selected.behavior;
  return /* @__PURE__ */ jsx(RowFilters, {
    el: section,
    entry,
    event: behavior.event,
    index,
    filters: behavior.filters,
    selectedFiltersOperator: behavior.filtersOperator,
    extraClass: { "for-behaviors": true },
    handleUpdate: (filters, filtersOperator, queryReason) => handleSelect(
      produce(selected, (item) => {
        item.behavior.filters = filters;
        item.behavior.filtersOperator = filtersOperator;
      }),
      queryReason
    ),
    sectionAttrs: {
      "data-groups-enabled": false,
      "allow-cohort-filter": false,
      "can-rename-step": false,
      "project-has-people": false,
      "project-has-cohorts": false,
      "hide-events": false,
      "include-custom-properties": `all`
    }
  });
}
